import { CLAIM_STATUS, normalizeClaimWorkflowStatus } from "../domain/claimWorkflow";

const CLAIM_AUDIT_EVENT_LABELS = {
  claim_created: "Claim created",
  submitted_for_review: "Submitted for review",
  assigned_to_reviewer: "Assigned to reviewer",
  review_notes_added: "Added review notes",
  status_changed: "Status changed",
  approved: "Approved",
  rejected: "Rejected",
  amendment_requested: "Amendment requested",
  pending_cfo: "Pending CFO",
  manual_review_completed: "Claim review completed",
  first_review_approved: "Approved in first review",
  first_review_approved_direct_to_cfo: "Approved in pending review and sent to CFO",
  first_review_rejected: "Rejected in first review",
  first_review_amendment_requested: "Amendment requested in first review",
  cfo_approval_requested: "Requested CFO approval",
  second_review_rejected: "Rejected in review",
  second_review_amendment_requested: "Amendment requested in review",
  cfo_approved: "Approved",
  cfo_rejected: "Rejected by CFO",
  claim_resubmitted: "Resubmitted claim",
  claim_moved_to_pending: "Moved to pending review",
  claim_invoice_replaced: "Invoice replaced",
  claim_ocr_rerun: "OCR rerun",
  draft_deleted: "Draft deleted",
  claim_marked_paid: "Marked as paid",
  partial_payment_recorded: "Partial payment recorded",
  partial_payment_completed_without_balance:
    "Completed without further payment",
};

const firstNonEmptyString = (...candidates) => {
  for (const candidate of candidates) {
    if (candidate == null) continue;
    if (typeof candidate === "object") {
      const nested = firstNonEmptyString(
        candidate.name,
        candidate.fullName,
        candidate.displayName,
        candidate.email,
      );
      if (nested) return nested;
      continue;
    }
    const value = String(candidate).trim();
    if (value) return value;
  }
  return "";
};

const getClaimActorLabel = (claim) => {
  const displayName = firstNonEmptyString(
    claim?.claimRequesterLabel,
    claim?.createdByName,
    claim?.submittedByName,
    claim?.requesterName,
    claim?.claimantName,
    claim?.requestedByName,
    claim?.submitterName,
    claim?.userDisplayName,
    claim?.requester,
    typeof claim?.submittedBy === "object" ? claim.submittedBy : null,
  );
  if (displayName) return displayName;
  return (
    firstNonEmptyString(
      claim?.createdBy,
      typeof claim?.submittedBy === "string" ? claim.submittedBy : null,
      claim?.userEmail,
      claim?.email,
      claim?.requestedBy,
    ) || "—"
  );
};

const getStageActionLabel = (decision, stage) => {
  const key = String(decision || "").trim().toLowerCase();
  if (stage === "first") {
    if (key === "approve") return "Approved in first review";
    if (key === "reject") return "Rejected in first review";
    if (key === "amend") return "Requested amendment";
  }
  if (stage === "second") {
    if (key === "request_cfo") return "Requested CFO approval";
    if (key === "reject") return "Rejected in higher review";
    if (key === "amend") return "Requested amendment";
  }
  if (stage === "final") {
    if (key === "approve") return "Approved";
    if (key === "reject") return "Rejected";
  }
  return "Updated claim";
};

export const formatClaimAuditEventLabel = (eventType) => {
  const key = String(eventType || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  if (CLAIM_AUDIT_EVENT_LABELS[key]) return CLAIM_AUDIT_EVENT_LABELS[key];
  const raw = String(eventType || "").trim();
  if (!raw) return "—";
  return raw
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const formatClaimAuditDateTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  const yyyy = date.getFullYear();
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const dd = String(date.getDate()).padStart(2, "0");
  const hh = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd} ${hh}:${min}`;
};

export const buildDefaultClaimAuditRows = (claim) => {
  const createdBy = getClaimActorLabel(claim);
  const uploadTime = claim?.ocrAt || claim?.createdAt;
  const currentStatus = normalizeClaimWorkflowStatus(claim?.status);
  const submittedAt =
    currentStatus === CLAIM_STATUS.DRAFT
      ? null
      : claim?.submittedAt || claim?.submittedDate || null;
  const rows = [];

  if (uploadTime) {
    rows.push({
      key: "created",
      at: uploadTime,
      userLabel: createdBy,
      action: "Claim created",
      statusKey: CLAIM_STATUS.DRAFT,
    });
  }

  if (submittedAt) {
    rows.push({
      key: "submitted",
      at: submittedAt,
      userLabel: createdBy,
      action: "Submitted for review",
      statusKey: CLAIM_STATUS.PENDING,
    });
  }

  if (claim?.firstReviewDecision && claim?.firstReviewedAt) {
    rows.push({
      key: "first-review",
      at: claim.firstReviewedAt,
      userLabel: claim.firstReviewedBy || "Reviewer",
      action: getStageActionLabel(claim.firstReviewDecision, "first"),
      statusKey:
        claim.firstReviewDecision === "approve"
          ? CLAIM_STATUS.REVIEW
          : claim.firstReviewDecision === "reject"
            ? CLAIM_STATUS.REJECTED
            : CLAIM_STATUS.AMENDMENT_REQUESTED,
    });
  }

  if (claim?.secondReviewDecision && claim?.secondReviewedAt) {
    rows.push({
      key: "second-review",
      at: claim.secondReviewedAt,
      userLabel: claim.secondReviewedBy || "Reviewer",
      action: getStageActionLabel(claim.secondReviewDecision, "second"),
      statusKey:
        claim.secondReviewDecision === "request_cfo"
          ? CLAIM_STATUS.PENDING_CFO
          : claim.secondReviewDecision === "reject"
            ? CLAIM_STATUS.REJECTED
            : CLAIM_STATUS.AMENDMENT_REQUESTED,
    });
  }

  if (claim?.finalDecision && claim?.finalDecisionAt) {
    rows.push({
      key: "final-decision",
      at: claim.finalDecisionAt,
      userLabel: claim.finalDecisionBy || "Approver",
      action: getStageActionLabel(claim.finalDecision, "final"),
      statusKey:
        claim.finalDecision === "approve"
          ? CLAIM_STATUS.APPROVED
          : CLAIM_STATUS.REJECTED,
    });
  } else if (claim?.manualReviewNotes || claim?.manualReviewDecision) {
    rows.push({
      key: "notes",
      at:
        claim?.manualReviewedAt ||
        claim?.updatedAt ||
        claim?.reviewedAt ||
        submittedAt ||
        uploadTime,
      userLabel:
        claim?.manualReviewedBy ||
        claim?.reviewedByName ||
        claim?.reviewedBy ||
        claim?.processedBy ||
        "Reviewer",
      action: "Added review notes",
      statusKey: CLAIM_STATUS.REVIEW,
    });
  }

  return rows.sort(
    (a, b) => new Date(a.at || 0).getTime() - new Date(b.at || 0).getTime(),
  );
};

export const getClaimAuditStatusPillClass = (statusKey) => {
  const normalized = String(statusKey || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
  const base = "fd-claim-details-audit-pill";
  if (normalized === "paid" || normalized === "approved" || normalized === "settled") {
    return `${base} ${base}--paid`;
  }
  if (normalized === "rejected" || normalized === "denied" || normalized === "declined") {
    return `${base} ${base}--rejected`;
  }
  if (
    normalized === "pending" ||
    normalized === "pending_cfo" ||
    normalized === "pending_manual_review" ||
    normalized === "amendment_requested"
  ) {
    return `${base} ${base}--pending`;
  }
  if (normalized === "draft") {
    return `${base} ${base}--draft`;
  }
  if (normalized === "review" || normalized === "in_review" || normalized === "processing") {
    return `${base} ${base}--review`;
  }
  return `${base} ${base}--neutral`;
};
